import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  Bot, 
  Trash2, 
  Terminal,
  ShieldAlert,
  GraduationCap,
  Sparkles,
  ChevronRight,
  Code2
} from 'lucide-react';
import { toast } from 'sonner';
import { getStreamingAIResponse, clearConversationHistory } from '@/lib/AIBrainService';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export default function SovereignAIEngine() {
  const [messages, setMessages] = useState<Message[]>(() => {
    const saved = localStorage.getItem('monarch_ai_chat_history');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return [{
      id: 'welcome',
      role: 'assistant',
      content: 'Приветствую. Я — суверенное ядро Monarch AI v4.0 Apex. Я готов провести детальный разбор кода, объяснить математические и архитектурные концепции, проанализировать соответствие стандартам UzDigComp-AI или провести сессию технической подготовки. Чем могу помочь?',
      timestamp: Date.now()
    }];
  });

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('monarch_ai_chat_history', JSON.stringify(messages));
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const handleSend = async (customPrompt?: string) => {
    const textToSend = (customPrompt || input).trim();
    if (!textToSend || isLoading) return;

    setInput('');
    const userMsg: Message = {
      id: String(Date.now()),
      role: 'user',
      content: textToSend,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setStreamingContent('');

    try {
      let accumulated = '';
      await getStreamingAIResponse(
        textToSend,
        (token) => {
          accumulated += token;
          setStreamingContent(accumulated);
        },
        undefined,
        undefined
      );

      const aiMsg: Message = {
        id: String(Date.now() + 1),
        role: 'assistant',
        content: accumulated || 'Ответ сформирован.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);
      setStreamingContent('');
    } catch {
      toast.error('Сбой связи с моделью. Проверьте подключение к сети.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    clearConversationHistory();
    setMessages([{
      id: 'welcome_reset',
      role: 'assistant',
      content: 'Контекст диалога очищен. Готов к новой аналитической задаче.',
      timestamp: Date.now()
    }]);
    toast.success('История диалога сброшена');
  };

  return (
    <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white text-[#07030e] flex items-center justify-center font-black text-sm shadow-xl shadow-white/10">
            AI
          </div>
          <div>
            <h2 className="text-lg font-bold text-white uppercase tracking-wider">
              Monarch AI Apex · Интеллектуальный аналитический центр
            </h2>
            <div className="text-xs text-white/50">
              Архитектурный ментор, эксперт по коду и стандартам UzDigComp-AI
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="glass-btn-secondary px-3 py-1.5 rounded-xl text-xs uppercase tracking-wider flex items-center gap-1.5"
            title="Очистить историю диалога"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Очистить</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <button
          type="button"
          onClick={() => handleSend('Проведи аудит моего решения и укажи слабые места в алгоритме и безопасности.')}
          className="glass-panel-subtle p-3 rounded-xl text-left hover:border-white/30 transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-white/80">
            <Code2 className="w-4 h-4 text-white/60" />
            <span>Аудит алгоритма и PEP8</span>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white" />
        </button>

        <button
          type="button"
          onClick={() => handleSend('Как стандарты цифровой грамотности UzDigComp-AI внедряются в банковских системах?')}
          className="glass-panel-subtle p-3 rounded-xl text-left hover:border-white/30 transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-white/80">
            <GraduationCap className="w-4 h-4 text-white/60" />
            <span>Методология UzDigComp</span>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white" />
        </button>

        <button
          type="button"
          onClick={() => handleSend('Какие ключевые протоколы кибербезопасности обязан соблюдать соискатель в FinTech?')}
          className="glass-panel-subtle p-3 rounded-xl text-left hover:border-white/30 transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-white/80">
            <ShieldAlert className="w-4 h-4 text-white/60" />
            <span>Безопасность в FinTech</span>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white" />
        </button>
      </div>

      <div 
        ref={chatScrollRef}
        className="glass-panel-subtle rounded-2xl p-4 sm:p-6 min-h-[300px] max-h-[460px] overflow-y-auto space-y-4"
      >
        {messages.map((m) => {
          const isAI = m.role === 'assistant';
          return (
            <div
              key={m.id}
              className={`flex flex-col ${isAI ? 'items-start' : 'items-end'}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 px-1">
                {isAI ? 'Monarch AI Engine' : 'Вы'}
              </div>
              <div className={`max-w-[88%] rounded-2xl p-4 text-xs sm:text-sm leading-relaxed ${
                isAI
                  ? 'bg-white/[0.06] text-white border border-white/10'
                  : 'bg-white text-[#07030e] font-medium shadow-lg shadow-white/15'
              }`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          );
        })}

        {streamingContent && (
          <div className="flex flex-col items-start">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 px-1">
              Monarch AI Engine (генерация)
            </div>
            <div className="max-w-[88%] rounded-2xl p-4 text-xs sm:text-sm leading-relaxed bg-white/[0.06] text-white border border-white/10 whitespace-pre-wrap">
              {streamingContent}
            </div>
          </div>
        )}

        {isLoading && !streamingContent && (
          <div className="text-xs text-white/50 px-2 flex items-center gap-2">
            <Terminal className="w-4 h-4 animate-pulse" />
            <span>Ядро формирует аналитический ответ...</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Сформулируйте запрос или прикрепите фрагмент кода..."
          className="flex-1 bg-white/[0.04] border border-white/15 rounded-2xl px-4 py-3 text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/50 resize-none"
        />

        <button
          type="button"
          onClick={() => handleSend()}
          disabled={isLoading || !input.trim()}
          className="glass-btn-primary px-6 py-3 rounded-2xl text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
          <span>Отправить</span>
        </button>
      </div>
    </div>
  );
}

